/**
 * Device ID Generator for Amazon SNS compatibility
 * iOS device tokens must be no more than 400 hexadecimal characters
 */

/**
 * Generate a device ID that meets Amazon SNS requirements
 * @returns {string} A hexadecimal device ID (typically 16-20 chars)
 */
export const generateDeviceId = () => {
  // Use timestamp as base (converted to hex)
  const timestamp = Date.now().toString(16)
  
  // Add random component (8 hex chars)
  const randomPart = Math.random().toString(16).substr(2, 8)
  
  // Combine timestamp and random part
  const deviceId = timestamp + randomPart
  
  // Ensure it's within Amazon SNS limits (400 chars max)
  if (deviceId.length > 400) {
    // If somehow too long, truncate to 400 chars
    return deviceId.substring(0, 400)
  }
  
  return deviceId
}

/**
 * Validate device ID length for Amazon SNS compatibility
 * @param {string} deviceId - The device ID to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validateDeviceId = (deviceId) => {
  if (!deviceId) return false
  
  // Check length (must be <= 400 chars)
  if (deviceId.length > 400) return false
  
  // Check if it's hexadecimal (optional but recommended)
  const hexRegex = /^[0-9a-fA-F]+$/
  return hexRegex.test(deviceId)
}

/**
 * Get or generate a device ID from localStorage
 * @returns {string} A valid device ID
 */
export const getOrGenerateDeviceId = () => {
  // Try to get existing device ID from localStorage
  let deviceId = localStorage.getItem('deviceId')
  
  // If no existing ID or invalid, generate new one
  if (!deviceId || !validateDeviceId(deviceId)) {
    deviceId = generateDeviceId()
    localStorage.setItem('deviceId', deviceId)
  }
  
  return deviceId
}

/**
 * Clear device ID from localStorage
 */
export const clearDeviceId = () => {
  localStorage.removeItem('deviceId')
}

/**
 * Get device ID info for debugging
 * @param {string} deviceId - The device ID to analyze
 * @returns {object} Device ID information
 */
export const getDeviceIdInfo = (deviceId) => {
  if (!deviceId) return null
  
  return {
    deviceId,
    length: deviceId.length,
    isValid: validateDeviceId(deviceId),
    isWithinSNSLimit: deviceId.length <= 400,
    timestamp: deviceId.length >= 13 ? parseInt(deviceId.substring(0, 13), 16) : null,
    randomPart: deviceId.length >= 8 ? deviceId.substring(deviceId.length - 8) : null
  }
}
